import React from 'react';
import Feature from '../feature/Feature';
import { type paths } from '../../../../endpoint/Schema';
import { Icons } from '../../../../assets/icons';
import { Button } from 'primereact/button';

// Helper function to wrap selected text with bold tags in contentEditable
const wrapWithBold = () => {
  document.execCommand('bold', false);
};

// Helper function to strip HTML tags for validation
const stripHtmlTags = (html: string): string => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html ?? '';
  return tmp.textContent ?? tmp.innerText ?? '';
};

interface IFlexpageFeatureProps {
  featureSettings:
    | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
    | undefined;
  setFeatureSettings: React.Dispatch<
    React.SetStateAction<
      | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
      | undefined
    >
  >;
}

interface IContentItem {
  title: string;
  description: string;
  order?: number;
}

export default function FlexpageFeature({
  featureSettings,
  setFeatureSettings,
}: IFlexpageFeatureProps) {
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [editingMain, setEditingMain] = React.useState(false);
  const [addingNew, setAddingNew] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState('');
  const [newDescription, setNewDescription] = React.useState('');
  const [editTitle, setEditTitle] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

  // Main flex page title and description
  const [mainTitle, setMainTitle] = React.useState(
    featureSettings?.flexPageSettings?.title ?? '',
  );
  const [mainDescription, setMainDescription] = React.useState(
    featureSettings?.flexPageSettings?.description ?? '',
  );

  const contents = featureSettings?.flexPageSettings?.contents ?? [];

  React.useEffect(() => {
    setMainTitle(featureSettings?.flexPageSettings?.title ?? '');
    setMainDescription(featureSettings?.flexPageSettings?.description ?? '');
  }, [
    featureSettings?.flexPageSettings?.title,
    featureSettings?.flexPageSettings?.description,
  ]);

  function handleToggleChecked(checked: boolean) {
    setFeatureSettings((prev) => ({
      ...prev,
      flexPageSettings: {
        ...prev?.flexPageSettings,
        enabled: checked,
        title: prev?.flexPageSettings?.title ?? '',
        description: prev?.flexPageSettings?.description ?? '',
      },
    }));
  }

  function handleMainTitleChange(value: string) {
    setMainTitle(value);
    setFeatureSettings((prev) => ({
      ...prev,
      flexPageSettings: {
        ...prev?.flexPageSettings,
        enabled: prev?.flexPageSettings?.enabled ?? false,
        title: value,
        description: prev?.flexPageSettings?.description ?? '',
      },
    }));
  }

  function handleMainDescriptionChange(value: string) {
    setMainDescription(value);
    setFeatureSettings((prev) => ({
      ...prev,
      flexPageSettings: {
        ...prev?.flexPageSettings,
        enabled: prev?.flexPageSettings?.enabled ?? false,
        title: prev?.flexPageSettings?.title ?? '',
        description: value,
      },
    }));
  }

  function addNewContent() {
    if (
      stripHtmlTags(newTitle).trim() &&
      stripHtmlTags(newDescription).trim()
    ) {
      const currentContents = featureSettings?.flexPageSettings?.contents ?? [];
      const nextOrder =
        currentContents.length > 0
          ? Math.max(...currentContents.map((c) => c.order ?? 0)) + 1
          : 0;

      const newContent: IContentItem = {
        title: newTitle.trim(),
        description: newDescription.trim(),
        order: Math.min(nextOrder, 3), // Cap at 3
      };

      setFeatureSettings((prev) => ({
        ...prev,
        flexPageSettings: {
          ...prev?.flexPageSettings,
          enabled: prev?.flexPageSettings?.enabled ?? false,
          title: prev?.flexPageSettings?.title ?? '',
          description: prev?.flexPageSettings?.description ?? '',
          contents: [...(prev?.flexPageSettings?.contents ?? []), newContent],
        },
      }));

      setNewTitle('');
      setNewDescription('');
      setAddingNew(false);
    }
  }

  function deleteContent(index: number) {
    setFeatureSettings((prev) => {
      const updatedContents = (prev?.flexPageSettings?.contents ?? [])
        .filter((_, i) => i !== index)
        .map((item, idx) => ({ ...item, order: idx })); // Reassign orders after deletion

      return {
        ...prev,
        flexPageSettings: {
          ...prev?.flexPageSettings,
          enabled: prev?.flexPageSettings?.enabled ?? false,
          contents: updatedContents,
        },
      };
    });
  }

  function startEdit(index: number) {
    const content = contents[index];
    if (content) {
      setEditTitle(content.title);
      setEditDescription(content.description);
      setEditingIndex(index);
    }
  }

  function saveEdit() {
    if (
      editingIndex !== null &&
      stripHtmlTags(editTitle).trim() &&
      stripHtmlTags(editDescription).trim()
    ) {
      setFeatureSettings((prev) => ({
        ...prev,
        flexPageSettings: {
          ...prev?.flexPageSettings,
          enabled: prev?.flexPageSettings?.enabled ?? false,
          contents: (prev?.flexPageSettings?.contents ?? []).map((item, i) =>
            i === editingIndex
              ? {
                  title: editTitle.trim(),
                  description: editDescription.trim(),
                  order: item.order ?? i, // Preserve order
                }
              : item,
          ),
        },
      }));

      setEditingIndex(null);
      setEditTitle('');
      setEditDescription('');
    }
  }

  function cancelEdit() {
    setEditingIndex(null);
    setEditTitle('');
    setEditDescription('');
  }

  function cancelAdd() {
    setAddingNew(false);
    setNewTitle('');
    setNewDescription('');
  }

  function handleDragStart(index: number) {
    setDraggedIndex(index);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  function handleDrop(dropIndex: number) {
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    setFeatureSettings((prev) => {
      const contents = [...(prev?.flexPageSettings?.contents ?? [])];
      const [draggedItem] = contents.splice(draggedIndex, 1);
      contents.splice(dropIndex, 0, draggedItem);

      // Reassign orders based on new positions (0-3)
      const updatedContents = contents.map((item, idx) => ({
        ...item,
        order: idx,
      }));

      return {
        ...prev,
        flexPageSettings: {
          ...prev?.flexPageSettings,
          enabled: prev?.flexPageSettings?.enabled ?? false,
          contents: updatedContents,
        },
      };
    });

    setDraggedIndex(null);
  }

  function handleDragEnd() {
    setDraggedIndex(null);
  }

  return (
    <Feature
      icon={Icons.ic_insight}
      image="img_insight"
      title="Flexpage"
      description="The Flexpage feature provides a flexible content management system that allows administrators to configure dynamic content layouts and interactive elements. This feature enables customization of app sections with various content types and user interaction components."
      checked={featureSettings?.flexPageSettings?.enabled ?? false}
      onChange={handleToggleChecked}
    >
      <div className="max-md:max-w-full">
        <div className="w-full px-20 pb-10 transition-all">
          <div className="flex flex-col p-5 mt-6 rounded-xl bg-whiteSmoke">
            <div className="flex flex-col gap-6">
              {/* Main Flexpage Settings */}
              {featureSettings?.flexPageSettings?.enabled && (
                <div className="p-4 surface-card border-round">
                  <div className="flex justify-content-between align-items-center mb-4">
                    <h3 className="font-semibold text-900 mb-0">
                      Flexpage Settings
                    </h3>
                    {!editingMain && (
                      <Button
                        className="p-button-text p-button-sm"
                        onClick={() => {
                          setEditingMain(true);
                        }}
                        tooltip="Edit"
                      >
                        <img
                          src={Icons.ic_edit}
                          alt="Edit"
                          className="w-4 h-4"
                        />
                      </Button>
                    )}
                  </div>

                  {editingMain ? (
                    /* Edit Mode */
                    <div className="flex flex-column gap-3">
                      <div>
                        <label className="block text-sm font-medium text-700 mb-1">
                          Title <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-column gap-2">
                          <Button
                            label="Bold"
                            icon="pi pi-bold"
                            className="p-button-sm p-button-outlined"
                            style={{ width: 'fit-content' }}
                            onClick={wrapWithBold}
                          />
                          <div
                            contentEditable
                            dangerouslySetInnerHTML={{
                              __html: mainTitle || '',
                            }}
                            onInput={(e) => {
                              handleMainTitleChange(e.currentTarget.innerHTML);
                            }}
                            style={{
                              minWidth: '400px',
                              width: '100%',
                              minHeight: '80px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              padding: '0.75rem',
                              outline: 'none',
                            }}
                            className="p-component"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-700 mb-1">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-column gap-2">
                          <Button
                            label="Bold"
                            icon="pi pi-bold"
                            className="p-button-sm p-button-outlined"
                            style={{ width: 'fit-content' }}
                            onClick={wrapWithBold}
                          />
                          <div
                            contentEditable
                            dangerouslySetInnerHTML={{
                              __html: mainDescription || '',
                            }}
                            onInput={(e) => {
                              handleMainDescriptionChange(
                                e.currentTarget.innerHTML,
                              );
                            }}
                            style={{
                              minWidth: '400px',
                              width: '100%',
                              minHeight: '120px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              padding: '0.75rem',
                              outline: 'none',
                            }}
                            className="p-component"
                          />
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          label="Done"
                          className="p-button-success p-button-sm"
                          onClick={() => {
                            setEditingMain(false);
                          }}
                          disabled={
                            !stripHtmlTags(mainTitle).trim() ||
                            !stripHtmlTags(mainDescription).trim()
                          }
                        />
                        <Button
                          label="Cancel"
                          className="p-button-secondary p-button-sm"
                          onClick={() => {
                            setMainTitle(
                              featureSettings?.flexPageSettings?.title ?? '',
                            );
                            setMainDescription(
                              featureSettings?.flexPageSettings?.description ??
                                '',
                            );
                            setEditingMain(false);
                          }}
                        />
                      </div>

                      {featureSettings?.flexPageSettings?.enabled &&
                        (!stripHtmlTags(mainTitle).trim() ||
                          !stripHtmlTags(mainDescription).trim()) && (
                          <small className="text-red-500">
                            Title and Description are required when flexpage is
                            enabled
                          </small>
                        )}
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex flex-column gap-3">
                      <div>
                        <label className="block text-sm font-medium text-700 mb-2">
                          Title
                        </label>
                        <div
                          className="text-900 p-3 surface-100 border-round"
                          dangerouslySetInnerHTML={{
                            __html: mainTitle || '<em>No title set</em>',
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-700 mb-2">
                          Description
                        </label>
                        <div
                          className="text-900 p-3 surface-100 border-round"
                          dangerouslySetInnerHTML={{
                            __html:
                              mainDescription || '<em>No description set</em>',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Content Management Section */}
              <div className="p-4 surface-card border-round">
                <div className="flex justify-content-between align-items-center mb-4">
                  <Button
                    label="Add New Content"
                    className="p-button-primary p-button-sm"
                    onClick={() => {
                      setAddingNew(true);
                    }}
                    disabled={contents.length >= 4}
                    tooltip={
                      contents.length >= 4
                        ? 'Maximum 4 content items allowed'
                        : ''
                    }
                  />
                  {contents.length >= 4 && (
                    <small className="text-orange-500 ml-2">
                      Maximum 4 items reached (orders 0-3)
                    </small>
                  )}
                </div>

                {/* Existing Content Items */}
                <div>
                  {contents.length > 0 && (
                    <div className="text-sm text-600 mb-3">
                      Drag and drop to reorder content (max 4 items, orders 0-3)
                    </div>
                  )}
                  {contents.map((content, index) => (
                    <div
                      key={index}
                      draggable={editingIndex !== index}
                      onDragStart={() => {
                        handleDragStart(index);
                      }}
                      onDragOver={handleDragOver}
                      onDrop={() => {
                        handleDrop(index);
                      }}
                      onDragEnd={handleDragEnd}
                      className={`border-1 border-300 border-round p-4 surface-50 mb-3 ${
                        draggedIndex === index ? 'opacity-50' : ''
                      } ${editingIndex !== index ? 'cursor-move' : ''}`}
                      style={{
                        transition: 'opacity 0.2s',
                      }}
                    >
                      {editingIndex === index ? (
                        /* Edit Mode */
                        <div className="flex flex-column gap-3">
                          <div>
                            <label className="block text-sm font-medium text-700 mb-1">
                              Title
                            </label>
                            <div className="flex flex-column gap-2">
                              <Button
                                label="Bold"
                                icon="pi pi-bold"
                                className="p-button-sm p-button-outlined"
                                style={{ width: 'fit-content' }}
                                onClick={wrapWithBold}
                              />
                              <div
                                contentEditable
                                dangerouslySetInnerHTML={{
                                  __html: editTitle || '',
                                }}
                                onInput={(e) => {
                                  setEditTitle(e.currentTarget.innerHTML);
                                }}
                                style={{
                                  minWidth: '400px',
                                  width: '100%',
                                  minHeight: '80px',
                                  border: '1px solid #ced4da',
                                  borderRadius: '4px',
                                  padding: '0.75rem',
                                  outline: 'none',
                                }}
                                className="p-component"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-700 mb-1">
                              Description
                            </label>
                            <div className="flex flex-column gap-2">
                              <Button
                                label="Bold"
                                icon="pi pi-bold"
                                className="p-button-sm p-button-outlined"
                                style={{ width: 'fit-content' }}
                                onClick={wrapWithBold}
                              />
                              <div
                                contentEditable
                                dangerouslySetInnerHTML={{
                                  __html: editDescription || '',
                                }}
                                onInput={(e) => {
                                  setEditDescription(e.currentTarget.innerHTML);
                                }}
                                style={{
                                  minWidth: '400px',
                                  width: '100%',
                                  minHeight: '100px',
                                  border: '1px solid #ced4da',
                                  borderRadius: '4px',
                                  padding: '0.75rem',
                                  outline: 'none',
                                }}
                                className="p-component"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              label="Save"
                              className="p-button-success p-button-sm"
                              onClick={saveEdit}
                              disabled={
                                !stripHtmlTags(editTitle).trim() ||
                                !stripHtmlTags(editDescription).trim()
                              }
                            />
                            <Button
                              label="Cancel"
                              className="p-button-secondary p-button-sm"
                              onClick={cancelEdit}
                            />
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div>
                          <div className="flex justify-content-between align-items-start">
                            <div className="flex gap-3 align-items-start flex-1">
                              <div
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: '#e3f2fd',
                                  borderRadius: '50%',
                                  color: '#1976d2',
                                  fontWeight: 'bold',
                                  fontSize: '14px',
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  lineHeight: '1',
                                }}
                              >
                                {content.order ?? index}
                              </div>
                              <div className="flex-1">
                                <h4
                                  className="font-medium text-900 mb-1"
                                  dangerouslySetInnerHTML={{
                                    __html: content.title,
                                  }}
                                />
                                <div
                                  className="text-600"
                                  dangerouslySetInnerHTML={{
                                    __html: content.description,
                                  }}
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                className="p-button-text p-button-sm"
                                onClick={() => {
                                  startEdit(index);
                                }}
                                tooltip="Edit"
                              >
                                <img
                                  src={Icons.ic_edit}
                                  alt="Edit"
                                  className="w-4 h-4"
                                />
                              </Button>
                              <Button
                                className="p-button-text p-button-danger p-button-sm"
                                onClick={() => {
                                  deleteContent(index);
                                }}
                                tooltip="Delete"
                              >
                                <img
                                  src={Icons.ic_delete}
                                  alt="Delete"
                                  className="w-4 h-4"
                                />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add New Content Form */}
                {addingNew && (
                  <div className="mt-4 border-1 border-300 border-round p-4 surface-100">
                    <h4 className="font-medium text-900 mb-3">
                      Add New Content
                    </h4>

                    {/* Title Input - Line 1 */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-700 mb-1">
                        Title
                      </label>
                      <div className="flex flex-column gap-2">
                        <Button
                          label="Bold"
                          icon="pi pi-bold"
                          className="p-button-sm p-button-outlined"
                          style={{ width: 'fit-content' }}
                          onClick={wrapWithBold}
                        />
                        <div
                          contentEditable
                          dangerouslySetInnerHTML={{ __html: newTitle || '' }}
                          onInput={(e) => {
                            setNewTitle(e.currentTarget.innerHTML);
                          }}
                          style={{
                            minWidth: '400px',
                            width: '100%',
                            minHeight: '80px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            padding: '0.75rem',
                            outline: 'none',
                          }}
                          className="p-component"
                        />
                      </div>
                    </div>

                    {/* Description Input - Line 2 */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-700 mb-1">
                        Description
                      </label>
                      <div className="flex flex-column gap-2">
                        <Button
                          label="Bold"
                          icon="pi pi-bold"
                          className="p-button-sm p-button-outlined"
                          style={{ width: 'fit-content' }}
                          onClick={wrapWithBold}
                        />
                        <div
                          contentEditable
                          dangerouslySetInnerHTML={{
                            __html: newDescription || '',
                          }}
                          onInput={(e) => {
                            setNewDescription(e.currentTarget.innerHTML);
                          }}
                          style={{
                            minWidth: '400px',
                            width: '100%',
                            minHeight: '100px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            padding: '0.75rem',
                            outline: 'none',
                          }}
                          className="p-component"
                        />
                      </div>
                    </div>

                    {/* Buttons - Line 3 */}
                    <div className="flex gap-2">
                      <Button
                        label="Add Content"
                        className="p-button-success p-button-sm"
                        onClick={addNewContent}
                        disabled={
                          !stripHtmlTags(newTitle).trim() ||
                          !stripHtmlTags(newDescription).trim()
                        }
                      />
                      <Button
                        label="Cancel"
                        className="p-button-secondary p-button-sm"
                        onClick={cancelAdd}
                      />
                    </div>
                  </div>
                )}

                {contents.length === 0 && !addingNew && (
                  <div className="text-center py-8 text-500">
                    No content items yet. Click &quot;Add New Content&quot; to
                    get started.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Feature>
  );
}
